import styles from "./Step4.module.css";
import SubmitButton from "../../../../../components/Button/SubmitButton/SubmitButton";
import { useOrderContext } from "../../../../../context/OrderContext";
import { useTranslation } from "react-i18next";
import { StepProps } from "../../../../../types/checkout.types";
import { createOrder } from "../../../../../services/cartService";
import { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { Subscriber } from "../../../../../types/auth.types";
import { useCartContext } from "../../../../../context/CartContext";
import { useUserContext } from "../../../../../context/UserContext";
import { useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import SecondaryButton from "../../../../../components/Button/SecondaryButton/SecondaryButton";
import CheckBox from "../../../../../components/CheckBox/CheckBox";
import Loader from "../../../../../components/Loader/Loader";
import { handleBackgroundScroll } from "../../../../../utils/backgroundScroll";
import { getCartPriceSummary, getCartPriceSummaryCurrency, groupCartData, getGroupedBlockCount, getSvozBlockCount, hasTransportForItems } from "../../../../../utils/cart";
import { normalizeDate, filterDatesOnOrAfterEffectiveTomorrow } from "../../../../../utils/calendar";
import { usePreferences } from "../../../../../context/PreferencesContext";
import { useStorageItems } from "../../../../../context/StorageItemsContext";
import { czk_eur_rate } from "../../../../../constants/prices";
import { useTimeModal } from "../../../../../context/TimeModalContext";
import { useQuickLinks } from "../../../../../hooks/useQuickLinks";
import { useProducts } from "../../../../../context/ProductContext";
import { BACKEND_URL } from "../../../../../services/apiConfig";
import { getAuthToken } from "../../../../../services/authUtils";
import { log, error as logError } from "../../../../../utils/logger";

interface CardPaymentResponse {
    payId?: string;
    redirectUrl?: string;
    error?: string;
    csob?: unknown;
}
export { BACKEND_URL };

/**
 * A Checkout step 4 component.
 * Contains order summary
 *
 * @param props StepProps
 * @param {() => void} props.onContinue - Increment step handler
 * @returns TSX.Element
 */
const Step4 = ({ onContinue, onBack, actionsContainer }: StepProps) => {
    const { quickLinks, setQuickLinks } = useQuickLinks(); // Quick links
    const { t } = useTranslation(); // Translation
    const { orderData, resetOrderData, orderType, handleOrder } = useOrderContext(); // Order data
    const { userData } = useUserContext();
    const { cartData, reloadCart } = useCartContext();
    const navigate = useNavigate();
    const [user, setUser] = useState<Subscriber>();
    const [termsAgreed, setTermsAgreed] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const { setLastTime } = useTimeModal();
    const { allProducts, packages, loading: loadingProducts, reloadProducts } = useProducts(); // Products

    const cartDatesISO = useMemo(() => {
        const raw = cartData?.selected_dates ?? [];
        return filterDatesOnOrAfterEffectiveTomorrow(raw.map((d) => normalizeDate(d)));
    }, [cartData?.selected_dates]);

    const selectedDatesForUI = cartDatesISO.length > 0
        ? cartDatesISO
        : filterDatesOnOrAfterEffectiveTomorrow(orderData.date);

    // Cart price helpers - will move to utils later
    const { selectedCurrency } = usePreferences(); // Current selected currency
    const { items, reloadItems, loading: loadingItems } = useStorageItems(); // Storage items

    // Reload products if they are loaded but dont appear
    useEffect(() => {
        if (!loadingProducts && !allProducts) {
        reloadProducts();
        }
        if (!loadingItems && !items) {
        reloadItems();
        }
    }, [loadingItems, loadingProducts]);

    const groupedCart = groupCartData(cartData ?? null);
    const groupedBlocksCount = getGroupedBlockCount(groupedCart);

    // Handle transport - svoz
    const [transportEx, setTransportEx] = useState<boolean>(false);
    useEffect(() => {
        if (!cartData?.items) return;
        const hasTransport = hasTransportForItems(cartData.items, {
            sales: allProducts,
            packages,
            storage: items,
        });
        setTransportEx(hasTransport);
    }, [cartData?.items, allProducts, packages, items]);

    const priceSummary = useMemo(
        () => getCartPriceSummary(cartData, groupedBlocksCount, getSvozBlockCount(groupedCart, { sales: allProducts, packages, storage: items })),
        [cartData, groupedBlocksCount, groupedCart, allProducts, packages, items]
    );
    const priceSummaryCurrency = useMemo(
        () => getCartPriceSummaryCurrency(priceSummary, selectedCurrency, czk_eur_rate),
        [priceSummary, selectedCurrency]
    );

    // Card Payment stuff
    const total = priceSummaryCurrency.totalWithTax.toFixed(2);

    const [cardLoading, setCardLoading] = useState(false);
    const [cardError, setCardError] = useState<string | null>(null);

    const startPayment = async () => {
        if (cardLoading) return; // guard double click
        setCardLoading(true);
        setCardError(null);

        try {
        const totalAmount = Math.round(Number(total) * 100);
        const curr = selectedCurrency === "eur" ? "EUR" : "CZK";

        const resp = await fetch(`${BACKEND_URL}/eshop/payment_gate/api/csob-init`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${getAuthToken()}`,
            },
            // totalAmount is recomputed server-side (ignored here); sent for logging only.
            body: JSON.stringify({ orderNo: cartData?.id, totalAmount, currency: curr }),
        });

        const contentType = resp.headers.get("content-type") || "";
        if (!contentType.includes("application/json")) {
            const text = await resp.text().catch(() => "");
            throw new Error(`Gateway init failed (${resp.status}). ${text?.slice(0, 200) || ""}`);
        }

        const data: CardPaymentResponse = await resp.json();

        if (resp.ok && data.redirectUrl) {
            window.location.assign(data.redirectUrl);
            return;
        }

        throw new Error(data.error || "Unknown error initiating payment");
        } catch (e: any) {
        logError("Payment initiation failed", e);
        setCardError(e?.message || "Payment initiation failed.");
        } finally {
        setCardLoading(false);
        }
    };
    // Card Payment stuff

    useEffect(() => {
        const u = userData?.subscribers?.find((sub) => sub.subscriber_id === orderData.recipient);
        setUser(u);
    }, [userData]);

    // Sample payment options
    const paymentMap = {
        card: t("checkout.steps.3.inputs.payment_methods.card"),
        card_on_delivery: t("checkout.steps.3.inputs.payment_methods.card_on_delivery"),
        cash_on_delivery: t("checkout.steps.3.inputs.payment_methods.cash_on_delivery"),
        invoice: t("checkout.steps.3.inputs.payment_methods.invoice"),
    };

    const saveQuickLinkForSubscriber = () => {
        if (!orderData.recipient) return;

        const subscriberId = orderData.recipient;

        const name = orderData.receiver?.trim();
        const phone = orderData.phone?.trim();
        const floor = orderData.floor;
        const room = orderData.room?.trim();

        if (!name && !phone && floor === undefined && !room) return;

        setQuickLinks((prev) => {
        const existing = prev.find((link) => link.subscriber === subscriberId);

        const addUnique = <T,>(arr: T[], value: T | undefined | null) =>
            value !== undefined && value !== null && value !== "" && !arr.includes(value) ? [...arr, value] : arr;

        if (existing) {
            return prev.map((link) =>
            link.subscriber === subscriberId
                ? {
                    ...link,
                    names: addUnique(link.names, name),
                    numbers: addUnique(link.numbers, phone),
                    floors: addUnique(link.floors, floor),
                    rooms: addUnique(link.rooms, room),
                }
                : link
            );
        } else {
            return [
            ...prev,
            {
                subscriber: subscriberId,
                names: name ? [name] : [],
                numbers: phone ? [phone] : [],
                floors: floor !== undefined ? [floor] : [],
                rooms: room ? [room] : [],
            },
            ];
        }
        });
    };

    // Checkout
    const handleCheckout = async () => {
        if (!orderData.paymentMethod) return;
        setLoading(true);
        handleBackgroundScroll(true);

        if (orderData.paymentMethod === "card") {
        startPayment();
        } else {
        const note = `telefon: ${orderData.phone} \n patro: ${
            orderData.floor !== undefined ? orderData.floor : ""
        } \n místnost: ${orderData.room} \n přebírající osoba: ${orderData.recipient} \n preferovaný čas: ${
            orderData.time
        } \n poznámka: ${orderData.note}`;

        setLastTime("");
        const res = await createOrder(
            orderData.paymentMethod,
            orderData.code,
            orderData.eventName ?? "",
            orderData.phone,
            orderData.floor !== undefined ? String(orderData.floor) : "",
            orderData.room ?? "",
            orderData.receiver,
            orderData.time ?? "",
            orderData.note ?? ""
        );

        saveQuickLinkForSubscriber();

        resetOrderData();
        navigate("/order-confirmation");
        }
        setLoading(false);
        handleBackgroundScroll(false);
    };

    return (
        <>
        {loading && <Loader text={t("checkout.steps.4.sending")} />}
        <div className={styles.CheckoutContent}>
            <div className={styles.CheckoutContentForm}>
            <p className={styles.CheckoutContentTitle}>
                {orderType === "demand" ? t("checkout.steps.4.demand_title") : t("checkout.steps.4.title")}
            </p>
            <div className={styles.CheckoutContentBody}>
                <div className={styles.CheckoutContentFormInputs}>
                <div className={styles.InfoGroup}>
                    <p className={styles.InfoGroupTitle}>
                    {orderType === "demand" ? t("checkout.steps.4.order_demand_title") : t("checkout.steps.4.order_title")}
                    </p>
                    <div className={styles.InfoGroupContent}>
                    <div className={styles.Info}>
                        <p className={styles.InfoTitle}>{t("checkout.steps.4.inputs.name")}</p>
                        <div className={styles.InfoContent}>
                        <p>{orderData.eventName ? orderData.eventName : t("checkout.steps.4.inputs.name")}</p>
                        </div>
                    </div>

                    <div className={styles.Info}>
                        <p className={styles.InfoTitle}>{t("checkout.steps.4.inputs.address")}</p>
                        <div className={styles.InfoContent}>
                        <p>{orderData.address ? orderData.address : t("checkout.steps.4.inputs.address")}</p>
                        </div>
                    </div>

                    <div className={styles.Info}>
                        <p className={styles.InfoTitle}>{t("checkout.steps.4.inputs.date")}</p>
                        <div className={styles.InfoContent}>
                        <p>
                            {selectedDatesForUI.length > 0
                            ? selectedDatesForUI.join(" | ")
                            : t("checkout.steps.4.inputs.date")}
                        </p>
                        </div>
                    </div>

                    <div className={styles.SplitInfo}>
                        <div className={styles.Info}>
                        <p className={styles.InfoTitle}>{t("checkout.steps.4.inputs.floor")}</p>
                        <div className={styles.InfoContent}>
                            <p>{orderData.floor !== undefined ? orderData.floor : t("checkout.steps.4.inputs.floor")}</p>
                        </div>
                        </div>

                        <div className={styles.Info}>
                        <p className={styles.InfoTitle}>{t("checkout.steps.4.inputs.room")}</p>
                        <div className={styles.InfoContent}>
                            <p>{orderData.room ? orderData.room : t("checkout.steps.4.inputs.room")}</p>
                        </div>
                        </div>
                    </div>

                    {transportEx && (
                        <div className={styles.Info}>
                        <p className={styles.InfoTitle}>{t("checkout.steps.2.preferred_time")}</p>
                        <div className={styles.InfoContent}>
                            <p>{orderData.time ? orderData.time : t("checkout.steps.2.preferred_time")}</p>
                        </div>
                        </div>
                    )}
                    </div>
                </div>

                <div className={styles.InfoGroup}>
                    <p className={styles.InfoGroupTitle}>
                    {orderType === "demand" ? t("checkout.steps.4.demand_receive_title") : t("checkout.steps.4.receive_title")}
                    </p>
                    <div className={styles.InfoGroupContent}>
                    <div className={styles.Info}>
                        <p className={styles.InfoTitle}>{t("checkout.steps.4.inputs.customer")}</p>
                        <div className={styles.InfoContent}>
                        <p>{user?.name ? user?.name : t("checkout.steps.4.inputs.customer")}</p>
                        </div>
                    </div>

                    <div className={styles.Info}>
                        <p className={styles.InfoTitle}>{t("checkout.steps.4.inputs.recipient")}</p>
                        <div className={styles.InfoContent}>
                        <p>{orderData.receiver ? orderData.receiver : t("checkout.steps.4.inputs.recipient")}</p>
                        </div>
                    </div>

                    <div className={styles.Info}>
                        <p className={styles.InfoTitle}>{t("checkout.steps.4.inputs.phone")}</p>
                        <div className={styles.InfoContent}>
                        <p>{orderData.phone ? orderData.phone : t("checkout.steps.4.inputs.phone")}</p>
                        </div>
                    </div>
                    </div>
                </div>

                <div className={styles.InfoGroup}>
                    <p className={styles.InfoGroupTitle}>{t("checkout.steps.4.billing_title")}</p>
                    <div className={styles.InfoGroupContent}>
                    <p className={styles.InfoGroupText}>{t("checkout.steps.4.inputs.billing_subtitle")}</p>
                    <div className={styles.Note}>
                        <p>{user?.name}</p>
                        <p>IČ: {user?.ic}</p>
                        {user?.dic && <p>DIČ: {user?.dic}</p>}
                        <p>{userData.email}</p>
                        <p>{cartData?.delivery_adress.adress}</p>
                        <p>{cartData?.delivery_adress.city}</p>
                    </div>

                    <div className={styles.Info}>
                        <p className={styles.InfoTitle}>{t("checkout.steps.4.inputs.payment")}</p>
                        <div className={styles.InfoContent}>
                        <p>
                            {orderData.paymentMethod ? paymentMap[orderData.paymentMethod] : t("checkout.steps.4.inputs.payment")}
                        </p>
                        </div>
                    </div>

                    <div className={styles.Info}>
                        <p className={styles.InfoTitle}>{t("checkout.steps.4.inputs.note")}</p>
                        <div className={styles.InfoContent}>
                        <p>{orderData.note ? orderData.note : ""}</p>
                        </div>
                    </div>

                    <div className={styles.Info}>
                        <p className={styles.InfoTitle}>{t("checkout.steps.4.inputs.code")}</p>
                        <div className={styles.InfoContent}>
                        <p>{orderData.code ? orderData.code : ""}</p>
                        </div>
                    </div>
                    </div>
                </div>
                </div>

                <div className={styles.ButtonGroupWrap}>
                <div className={styles.Agree}>
                    <CheckBox isCircle={true} checked={termsAgreed} onCheck={() => setTermsAgreed(!termsAgreed)} />
                    <p className={styles.AgreeText}>
                    {t("checkout.steps.4.agree_with")}{" "}
                    <span onClick={() => navigate("/terms")}>{t("checkout.steps.4.terms")}</span>
                    </p>
                </div>
                {!termsAgreed && (
                    <p className={styles.TermsRequired}>{t("checkout.steps.4.terms_required")}</p>
                )}

                {actionsContainer && createPortal(
                    <div className={styles.ButtonGroup}>
                        <SecondaryButton
                            text={t("buttons.back")}
                            showChevron={false}
                            colorVariant="primary"
                            onClick={() => {
                                onBack && onBack();
                            }}
                            Icon={ChevronLeft}
                            className={styles.BackButton}
                        />
                        <SubmitButton text={t("buttons.confirm_order")} onClick={handleCheckout} disabled={!termsAgreed} />
                    </div>,
                    actionsContainer
                )}
                </div>
            </div>
            </div>
        </div>
        </>
    );
};

export default Step4;
